import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function RecipientInput({ recipients = [], onChange, placeholder = "Add recipients..." }) {
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (input.length < 1) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/mail/suggest-recipients?q=${encodeURIComponent(input)}`, {
          headers: getHeaders(), withCredentials: true,
        });
        const results = (res.data.recipients || []).filter(
          (r) => !recipients.find((sr) => sr.user_id === r.user_id)
        );
        setSuggestions(results);
        setShowDropdown(results.length > 0);
      } catch {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [input, recipients]);

  const addRecipient = (recipient) => {
    onChange([...recipients, recipient]);
    setInput("");
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const removeRecipient = (userId) => {
    onChange(recipients.filter((r) => r.user_id !== userId));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && input.trim()) {
      addRecipient({ user_id: `ext_${Date.now()}`, name: input.trim(), email: input.trim() });
    }
    if (e.key === "Backspace" && !input && recipients.length > 0) {
      removeRecipient(recipients[recipients.length - 1].user_id);
    }
  };

  return (
    <div className="relative">
      <div className="flex flex-wrap items-center gap-1 min-h-[32px] p-1.5 border border-app rounded bg-app-card focus-within:border-violet-400">
        {recipients.map((r) => (
          <Badge key={r.user_id} variant="secondary" className="text-xs px-1.5 py-0 gap-1 bg-violet-600/20 text-violet-300 border border-violet-600/30">
            {r.name || r.email}
            <button onClick={() => removeRecipient(r.user_id)} className="hover:text-red-500">
              <X className="w-2.5 h-2.5" />
            </button>
          </Badge>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => input.length >= 1 && setShowDropdown(suggestions.length > 0)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
          placeholder={recipients.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[80px] text-xs border-none outline-none bg-transparent p-0.5"
        />
      </div>

      {showDropdown && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-app-card border border-app rounded shadow-lg max-h-40 overflow-auto">
          {suggestions.map((s) => (
            <button
              key={s.user_id}
              onMouseDown={() => addRecipient(s)}
              className="w-full text-left px-3 py-2 text-xs hover:bg-violet-600/10 flex items-center gap-2"
            >
              <div className="w-6 h-6 rounded-full bg-surface-item flex items-center justify-center text-[10px] font-medium text-app">
                {(s.name || s.email || "?")[0].toUpperCase()}
              </div>
              <div>
                <p className="font-medium text-app">{s.name || s.email}</p>
                {s.name && <p className="text-app-secondary">{s.email}</p>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
